import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, GripVertical, X, Clock, AlertCircle, Lightbulb, Edit, Check } from "lucide-react";

interface AgendaSectionProps {
  data: {
    agenda_items: any[];
  };
  onChange: (updates: any) => void;
  isFirstMeeting?: boolean;
}

export function AgendaSection({ data, onChange, isFirstMeeting }: AgendaSectionProps) {
  const [newItem, setNewItem] = useState({
    title: "",
    presenter: "",
    time_allocated: "",
    description: "",
  });

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editItem, setEditItem] = useState({
    title: "",
    presenter: "",
    time_allocated: "",
    description: "",
  });

  const startEditing = (index: number) => {
    const item = data.agenda_items[index];
    setEditItem({
      title: item.title || "",
      presenter: item.presenter || "",
      time_allocated: item.time_allocated || "",
      description: item.description || "",
    });
    setEditingIndex(index);
  };

  const cancelEditing = () => {
    setEditingIndex(null);
  };

  const updateAgendaItem = (index: number) => {
    const updated = [...data.agenda_items];
    updated[index] = {
      ...updated[index],
      title: editItem.title,
      presenter: editItem.presenter,
      time_allocated: editItem.time_allocated,
      description: editItem.description,
    };
    onChange({ agenda_items: updated });
    setEditingIndex(null);
  };

  const addAgendaItem = () => {
    if (!newItem.title.trim()) return;

    onChange({
      agenda_items: [...data.agenda_items, {
        ...newItem,
        order: data.agenda_items.length + 1,
        completed: false,
        discussion_notes: "",
      }],
    });

    setNewItem({
      title: "",
      presenter: "",
      time_allocated: "",
      description: "",
    });
  };

  const removeItem = (index: number) => {
    onChange({
      agenda_items: data.agenda_items.filter((_, i) => i !== index),
    });
  };

  const toggleComplete = (index: number) => {
    const updated = [...data.agenda_items];
    updated[index] = {
      ...updated[index],
      completed: !updated[index].completed,
    };
    onChange({ agenda_items: updated });
  };

  const updateDiscussionNotes = (index: number, notes: string) => {
    const updated = [...data.agenda_items];
    updated[index] = {
      ...updated[index],
      discussion_notes: notes,
    };
    onChange({ agenda_items: updated });
  };

  // Check if there are any pre-populated items
  const hasSuggestedItems = data.agenda_items.some((item: any) => item.is_suggested);
  const requiredCount = data.agenda_items.filter((item: any) => item.is_required).length;
  const suggestedCount = data.agenda_items.filter((item: any) => item.is_suggested && !item.is_required).length;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold mb-4">Agenda</h2>
        <p className="text-muted-foreground">
          Add agenda items and track discussions as the meeting progresses.
        </p>
      </div>

      {/* First Meeting Helper Banner */}
      {isFirstMeeting && (
        <Card className="p-4 bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800">
          <div className="flex items-start gap-3">
            <Lightbulb className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
            <div className="space-y-1">
              <p className="font-medium text-green-800 dark:text-green-200">
                ✓ Agenda Items Pre-Filled!
              </p>
              <p className="text-sm text-green-700 dark:text-green-300">
                Your agenda has been set up automatically based on the required resolutions for your first meeting.
                All items are already marked as complete - just review and proceed to the Motions section.
              </p>
            </div>
          </div>
        </Card>
      )}
      
      {/* Pre-populated Items Notice (for non-first meetings) */}
      {!isFirstMeeting && hasSuggestedItems && (
        <Card className="p-4 bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800">
          <div className="flex items-start gap-3">
            <Lightbulb className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
            <div className="space-y-1">
              <p className="font-medium text-amber-800 dark:text-amber-200">
                Pre-populated Agenda Items
              </p>
              <p className="text-sm text-amber-700 dark:text-amber-300">
                {requiredCount > 0 && (
                  <span>
                    <strong>{requiredCount} required resolution{requiredCount > 1 ? 's' : ''}</strong>
                    {suggestedCount > 0 ? ' and ' : ' '}
                  </span>
                )}
                {suggestedCount > 0 && (
                  <span>{suggestedCount} suggested resolution{suggestedCount > 1 ? 's' : ''}</span>
                )}
                {' '}have been added based on your journey step. You can edit, reorder, or remove them as needed.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Add New Agenda Item */}
      <Card className="p-4 space-y-4">
        <h3 className="font-semibold">Add Agenda Item</h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2 space-y-2">
            <Label htmlFor="item-title">Item Title *</Label>
            <Input
              id="item-title"
              value={newItem.title}
              onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
              placeholder="e.g., Financial Report Review"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="item-presenter">Presenter</Label>
            <Input
              id="item-presenter"
              value={newItem.presenter}
              onChange={(e) => setNewItem({ ...newItem, presenter: e.target.value })}
              placeholder="Who will present this item?"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="item-time">Time Allocated</Label>
            <Input
              id="item-time"
              value={newItem.time_allocated}
              onChange={(e) => setNewItem({ ...newItem, time_allocated: e.target.value })}
              placeholder="e.g., 15 minutes"
            />
          </div>

          <div className="col-span-2 space-y-2">
            <Label htmlFor="item-description">Description</Label>
            <Textarea
              id="item-description"
              value={newItem.description}
              onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
              placeholder="Brief description of this agenda item"
              rows={2}
            />
          </div>
        </div>

        <Button onClick={addAgendaItem} disabled={!newItem.title.trim()}>
          <Plus className="h-4 w-4 mr-2" />
          Add Item
        </Button>
      </Card>

      {/* Agenda Items List */}
      {data.agenda_items.length > 0 && (
        <div className="space-y-4">
          <h3 className="font-semibold">Agenda Items ({data.agenda_items.length})</h3>
          
          {data.agenda_items.map((item: any, index: number) => (
            <Card key={index} className="p-4 space-y-3">
              {editingIndex === index ? (
                /* Edit Mode */
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Edit className="h-4 w-4 text-primary" />
                    <span className="font-semibold text-sm">Editing Agenda Item</span>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor={`edit-title-${index}`}>Item Title *</Label>
                    <Input
                      id={`edit-title-${index}`}
                      value={editItem.title}
                      onChange={(e) => setEditItem({ ...editItem, title: e.target.value })}
                      placeholder="e.g., Financial Report Review"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor={`edit-presenter-${index}`}>Presenter</Label>
                      <Input
                        id={`edit-presenter-${index}`}
                        value={editItem.presenter}
                        onChange={(e) => setEditItem({ ...editItem, presenter: e.target.value })}
                        placeholder="Who will present this item?"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`edit-time-${index}`}>Time Allocated</Label>
                      <Input
                        id={`edit-time-${index}`}
                        value={editItem.time_allocated}
                        onChange={(e) => setEditItem({ ...editItem, time_allocated: e.target.value })}
                        placeholder="e.g., 15 minutes"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`edit-description-${index}`}>Description</Label>
                    <Textarea
                      id={`edit-description-${index}`}
                      value={editItem.description}
                      onChange={(e) => setEditItem({ ...editItem, description: e.target.value })}
                      placeholder="Brief description of this agenda item"
                      rows={2}
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button onClick={() => updateAgendaItem(index)} disabled={!editItem.title.trim()}>
                      <Check className="h-4 w-4 mr-2" />
                      Save Changes
                    </Button>
                    <Button variant="outline" onClick={cancelEditing}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                /* Display Mode */
                <div className="flex items-start gap-3">
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <GripVertical className="h-5 w-5 text-muted-foreground cursor-move" />
                    <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center text-sm font-semibold">
                      {index + 1}
                    </div>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-semibold">{item.title}</h4>
                          {item.is_required && (
                            <Badge variant="default" className="bg-amber-500 hover:bg-amber-600 text-white text-xs">
                              <AlertCircle className="h-3 w-3 mr-1" />
                              Required Resolution
                            </Badge>
                          )}
                          {item.is_suggested && !item.is_required && (
                            <Badge variant="outline" className="border-yellow-500 text-yellow-700 dark:text-yellow-400 text-xs">
                              <Lightbulb className="h-3 w-3 mr-1" />
                              Suggested Resolution
                            </Badge>
                          )}
                        </div>
                        {item.presenter && (
                          <p className="text-sm text-muted-foreground mt-1">
                            Presenter: {item.presenter}
                          </p>
                        )}
                        {item.time_allocated && (
                          <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                            <Clock className="h-3 w-3" />
                            {item.time_allocated}
                          </div>
                        )}
                        {item.description && (
                          <p className="text-sm text-muted-foreground mt-2">
                            {item.description}
                          </p>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          variant={item.completed ? "default" : "outline"}
                          size="sm"
                          onClick={() => toggleComplete(index)}
                        >
                          {item.completed ? "Completed ✓" : "Mark Complete"}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => startEditing(index)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeItem(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Discussion Notes */}
                    <div className="mt-3 space-y-2">
                      <Label htmlFor={`notes-${index}`}>Discussion Notes</Label>
                      <Textarea
                        id={`notes-${index}`}
                        value={item.discussion_notes || ""}
                        onChange={(e) => updateDiscussionNotes(index, e.target.value)}
                        placeholder="Record key points discussed for this agenda item..."
                        rows={3}
                        className="text-sm"
                      />
                    </div>
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
